/** /partners — the integration partner registry (dev-platform metaplan G1; GATE D2).
 *
 *  One row per partner: its status, whether its contract is recorded and in force
 *  (the gate a developer key needs), its sandbox, and the activity of the sandbox's
 *  keys. A row opens the partner's page; "New partner" starts the partner create
 *  path, which creates the partner and its sandbox and lands on the sandbox's setup. */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, MenuItem, TextField, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import * as partnersApi from '@/api/partners';
import type { Partner, PartnerStatus } from '@/api/partners';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { ResourceDataGrid, type ResourceDataGridParams } from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';

import { PartnerContractChip, PartnerStatusChip } from './partnerChips';

export const PARTNER_CREATE_PATH = '/tour-operators/new/partner';

export function PartnersPage() {
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_partners_manage');
  const [statusFilter, setStatusFilter] = useState<'any' | PartnerStatus>('any');

  const openCreate = () => navigate(PARTNER_CREATE_PATH);
  useCreateIntent(() => {
    if (canManage) openCreate();
  });

  const extra = useMemo(() => {
    const e: Record<string, string> = {};
    if (statusFilter !== 'any') e.status = statusFilter;
    return e;
  }, [statusFilter]);

  const columns = useMemo<GridColDef<Partner>[]>(
    () => [
      copyableColumn({ field: 'code', headerName: 'Code', width: 160 }),
      {
        field: 'name',
        headerName: 'Partner',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/partners/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 180,
        valueGetter: (_, row) => partnersApi.PARTNER_KIND_LABELS[row.kind],
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (p) => <PartnerStatusChip status={p.row.status} />,
      },
      {
        field: 'contract_term_end',
        headerName: 'Contract',
        width: 200,
        renderCell: (p) => <PartnerContractChip partner={p.row} />,
      },
      {
        field: 'sandbox',
        headerName: 'Sandbox',
        width: 180,
        sortable: false,
        renderCell: (p) =>
          p.row.sandbox ? (
            <RouterLink
              to={`/tour-operators/${p.row.sandbox.id}`}
              style={{ color: '#0a1a3f' }}
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.sandbox.code}
            </RouterLink>
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              None
            </Box>
          ),
      },
      {
        field: 'keys',
        headerName: 'Keys',
        width: 110,
        sortable: false,
        renderCell: (p) => (
          <Tooltip title={`${p.row.keys.active} active of ${p.row.keys.total}`}>
            <span>
              {p.row.keys.active} / {p.row.keys.total}
            </span>
          </Tooltip>
        ),
      },
      {
        field: 'last_seen',
        headerName: 'Last seen',
        width: 140,
        renderCell: (p) =>
          p.row.last_seen ? (
            <span title={p.row.last_seen}>{formatRelativeTime(p.row.last_seen)}</span>
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              Never
            </Box>
          ),
      },
      {
        field: 'contacts_count',
        headerName: 'Contacts',
        width: 100,
        sortable: false,
        renderCell: (p) => <Chip size="small" variant="outlined" label={p.row.contacts_count} />,
      },
    ],
    [],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      partnersApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'status',
      priority: 5,
      active: statusFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          sx={{ width: 170 }}
          onChange={(e) => setStatusFilter(e.target.value as 'any' | PartnerStatus)}
        >
          <MenuItem value="any">Any</MenuItem>
          {(Object.keys(partnersApi.PARTNER_STATUS_LABELS) as PartnerStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {partnersApi.PARTNER_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="partner-onboarding"
        title="Partners"
        subtitle="Integration partners with a contract: each works in its own sandbox, and only an active partner with a recorded contract in force may hold a developer key."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New partner
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<Partner>
        queryKey={['partners']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        defaultSort={{ field: 'name', sort: 'asc' }}
        emptyMessage="No partners yet."
        onRowClick={(id) => navigate(`/partners/${id}`)}
        headerToolbar={{
          onResetAllFilters: () => setStatusFilter('any'),
          resetDisabled: statusFilter === 'any',
          search: true,
          searchPlaceholder: 'Code, name, legal name…',
          exportFileName: 'partners',
        }}
      />
    </Box>
  );
}
